/**
 * Lazy bootstrap for chart libraries.
 *
 * Highcharts (~300KB+ raw) and Chart.js (~150KB+ raw) are only used by authed
 * dashboard/insights/skills views — never by login, signup, or onboarding.
 * Eagerly importing them in `main.ts` made every visitor to /signup pay for
 * code they will not run.
 *
 * `ensureChartLibs()` resolves immediately if the libs are already loaded,
 * starts a single shared promise on the first call, and is awaited from the
 * router's `beforeEach` guard for any route with `meta.auth`. This keeps the
 * familiar `Vue.use(HighchartsVue)` / `import 'chart.js/auto'` registration
 * — just deferred until the first time it could matter.
 */
let loaded = false
let loading: Promise<void> | null = null

export function ensureChartLibs(): Promise<void> {
  if (loaded) return Promise.resolve()
  if (loading) return loading
  loading = (async () => {
    await Promise.all([
      import(/* webpackChunkName: "chart-libs" */ '@/plugin/highcharts'),
      import(/* webpackChunkName: "chart-libs" */ '@/plugin/chart-js'),
    ])
    loaded = true
  })()
  return loading
}
